import {
  Box,
  Button,
  Card,
  ChakraProvider,
  Container,
  Grid,
  HStack,
  Stack,
  Text,
  useDisclosure,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { Menu } from "react-feather";
import OperationalWheel from "./operationalWheel";

import { useState } from "react";
import LucroFlowModal from "./flowPopUp";
import theme from "../../theme";
import ClientDashboardLayout from "../dashboard/dashboardLayout";
import ClientDashboardLayoutSaas from "../clientDashboard/ClientDashboardLayout";
import { Alert, Box as MuiBox, Typography } from "@mui/material";

export const OperationalFlow = ({ drillDownEnabled = true } = {}) => {
  const [isMobile] = useMediaQuery("(max-width: 1280px)");
  const [selectedMetric, setSelectedMetric] = useState(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const openLucroInsightModal = (metricName, desc) => {
    setSelectedMetric({ metricName, desc });
    onOpen();
  };

  // SAAS: use the shared ClientDashboardLayout header (matches `ReportsPage`)
  // and avoid Lucro Flow's fixed header bars that overlap the SAAS sidenav.
  if (!drillDownEnabled) {
    return (
      <ClientDashboardLayoutSaas
        title="Lucro Flow"
        contentMaxWidth="100%"
        contentAlign="left"
      >
        <ChakraProvider theme={theme}>
          <Box width="100%" overflowX="hidden">
            <LucroFlowModal
              metricName={selectedMetric?.metricName || ""}
              desc={selectedMetric?.desc || ""}
              isOpen={isOpen}
              onClose={onClose}
              drillDownEnabled={drillDownEnabled}
            />

            <Box sx={{ width: "100%" }}>
              <Alert
                severity="info"
                icon={
                  <MuiBox
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      backgroundColor: "#0288D11A",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MuiBox
                      component="img"
                      src="/infoIcon.svg"
                      sx={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  </MuiBox>
                }
                sx={{
                  borderRadius: "8px",
                  width: "100%",
                  mb: "4em",
                  alignItems: "center",
                  backgroundColor: "rgba(2, 136, 209, 0.08)",
                  border: "1px solid rgba(2, 136, 209, 0.12)",
                  "& .MuiAlert-icon": {
                    alignItems: "center",
                    display: "flex",
                    mt: 0,
                  },
                }}
              >
                <Typography
                  fontWeight={600}
                  sx={{ color: "#0288D1", fontFamily: "Rethink Sans" }}
                >
                  What is Lucro flow?
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(0,0,0,0.6)",
                    fontFamily: "Rethink Sans",
                  }}
                >
                  Follow a guided operating framework that links actions to
                  financial outcomes and priorities. Gain clear guidance on each
                  part of your business so you can operate efficiently and make
                  confident, informed decisions.
                </Typography>
              </Alert>

              <Box
                sx={{
                  // Keep wheel slightly scaled for SAAS, and add a small top offset
                  // so its SVG overflow doesn't collide with the banner above.
                  transformOrigin: "center top",
                  transform: `scale(${isMobile ? 0.93 : 0.95})`,
                  mt: "0px",
                  pb: isMobile ? "0px" : "0px",
                }}
              >
                <Stack alignItems="center" justifyContent="center">
                  <OperationalWheel
                    drillDownEnabled={drillDownEnabled}
                    onSegmentClick={(segment) => {
                      openLucroInsightModal(segment.subtitle, segment.desc);
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>
        </ChakraProvider>
      </ClientDashboardLayoutSaas>
    );
  }

  // Platform: keep the existing Lucro Flow header behavior.
  return (
    <ClientDashboardLayout>
      <ChakraProvider theme={theme}>
        <Container
          width="100%"
          maxW="100%"
          bg="#F4F4F2"
          p="0"
          minH="100vh"
          overflowX="hidden"
        >
          <Stack
            width="100%"
            maxW="100%"
            bg="#F4F4F2"
            p="0"
            minH="100vh"
            overflowX="hidden"
          >
            <LucroFlowModal
              metricName={selectedMetric?.metricName || ""}
              desc={selectedMetric?.desc || ""}
              isOpen={isOpen}
              onClose={onClose}
              drillDownEnabled={drillDownEnabled}
            />
            {isMobile && (
              <Card
                position="fixed"
                zIndex={600}
                borderRadius={0}
                h="72px"
                borderBottom="1px solid #e0e0e0"
                width="100%"
              >
                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  h="100%"
                  w="100%"
                  px={4}
                >
                  <Button
                    w="50px"
                    h="50px"
                    p={2}
                    bg="white"
                    _hover={{ color: "white" }}
                    _active={{ color: "white" }}
                    onClick={() => {
                      window.dispatchEvent(new Event("sidebar:expand"));
                    }}
                  >
                    <Menu
                      style={{ width: "50px", height: "100%" }}
                      color="black"
                    />
                  </Button>
                </HStack>
              </Card>
            )}

            {!isMobile && (
              <Card
                position="fixed"
                mt={isMobile ? "72px" : "0px"}
                borderRadius={0}
                width={isMobile ? "100%" : "auto"}
                height="75px"
                display="flex"
                alignItems="center"
                zIndex={600}
                borderBottom="1px solid #e0e0e0"
                mb={1}
              >
                <Grid
                  templateColumns="1fr 200fr 1fr"
                  width="100vw"
                  maxW="100vw"
                  alignItems="center"
                  justifyItems="start"
                  padding="0px 20px"
                  height="100%"
                >
                  <VStack
                    py={"8px"}
                    gridRow={1}
                    gridColumn="1 / span 3"
                    textAlign={"left"}
                  >
                    <Box gridColumn="2" justifySelf="start" textAlign="left">
                      <Text
                        fontWeight="700"
                        fontSize="24px"
                        color="#1A202C"
                        lineHeight="1.2"
                      >
                        Lucro Flow
                      </Text>
                      <Text fontSize="16px" color="#4A5568" lineHeight="1.3">
                        Gain clear guidance on each part of your business so you
                        can operate efficiently and make confident, informed
                        decisions.
                      </Text>
                    </Box>
                  </VStack>
                  <Box
                    gridRow={1}
                    gridColumn={3}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <HStack>
                      <></>
                    </HStack>
                  </Box>
                </Grid>
              </Card>
            )}
            <Stack
              mt="74px"
              p="50px"
              minH="calc(100vh - 74px)"
              w="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {isMobile && (
                <VStack
                  mb={"60px"}
                  spacing={2}
                  width={"100%"}
                  align="flex-start"
                  textAlign="left"
                >
                  <Text fontWeight="700" fontSize="24px" color="#1A202C">
                    Lucro Flow
                  </Text>
                  <Text fontSize="16px" color="#4A5568">
                    Gain clear guidance on each part of your business so you can
                    operate efficiently and make confident, informed decisions.
                  </Text>
                </VStack>
              )}
              <OperationalWheel
                drillDownEnabled={drillDownEnabled}
                onSegmentClick={(segment) => {
                  openLucroInsightModal(segment.subtitle, segment.desc);
                }}
              />
            </Stack>
          </Stack>
        </Container>
      </ChakraProvider>
    </ClientDashboardLayout>
  );
};
